import { captureException } from '@sentry/core';
import { PRODUCTION } from '@sigma/env/environments.js';
import { filterLoggerObject } from './filterLoggerObject.js';
/**
 * Wraps a log sink into the transport every deployed runtime registers, so the PHI boundary holds by
 * construction rather than by remembering which registration sites can reach production.
 *
 * It owns the two things a log entry does — write a line, and on `error` report to Sentry — because
 * they need *different* payloads, and keeping them in one place is what stops the two being confused
 * again (see below). Wrapping the whole of a transport that reports to Sentry itself is the shape
 * that caused that confusion.
 *
 * `PRODUCTION` is read as a build-time constant rather than taken as a parameter: `process.env.ENV`
 * is substituted with a literal in browser bundles (`getViteBaseConfig.ts`), so a non-prod bundle
 * folds the branch away and ships no filter at all.
 */
export function createTelemetryTransport(writeLog) {
    return (logger, level, message, obj) => {
        // The line goes first so that, in the browser, an error's own log entry is already in the
        // breadcrumb buffer by the time the Sentry event below is assembled.
        writeLog(logger, level, message, PRODUCTION ? filterLoggerObject(obj) : obj);
        if (level === 'error') {
            // Sentry gets the REAL error, never `projectError`'s output.
            //
            // A plain `{ name, message, stack }` fails the SDK's `isError` check, so `getException`
            // marks the exception `synthetic`, retitles the issue `'X' captured as exception with
            // message '…'`, and attaches the stack captured *here*, in the transport, instead of the
            // one from the throw site — the real stack survives only in `extra.__serialized__`, which
            // the `beforeSend` filter then collapses. It also leaves `hint.originalException` a plain
            // object, so `extractErrorData` returns undefined and the server-side `ApplicationError`
            // data-bag promotion never fires. `projectError` output is for the log line and nothing else.
            //
            // No environment gate: `captureException` returns early when no client is bound, so this is
            // a no-op wherever Sentry was never initialised — including under `bun tests`.
            //
            // No second argument, deliberately: `captureException`'s hint `data` key is never promoted
            // onto the event, and `{ extra: obj }` *would* be — that is the channel this boundary exists
            // to close.
            captureException(obj?.error ?? new Error(message));
        }
    };
}
//# sourceMappingURL=createTelemetryTransport.js.map